import { useEffect, useRef } from "react";

type Opts = { strength?: number; radius?: number };

/**
 * Magnetic-pull effect for CTAs. Attach the returned ref to the element.
 *
 * Perf:
 * - window pointermove only attached while the cursor is inside the element
 * - rAF-throttled DOM writes, no React re-renders
 * - bails on touch, reduced-motion, and updates live if user toggles either
 */
export function useMagnetic<T extends HTMLElement = HTMLElement>(opts: Opts = {}) {
  const { strength = 0.25, radius = 80 } = opts;
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const reduceMQ = window.matchMedia("(prefers-reduced-motion: reduce)");
    const hoverMQ = window.matchMedia("(hover: hover) and (pointer: fine)");

    let rect: DOMRect | null = null;
    let raf = 0;
    let tx = 0;
    let ty = 0;
    let active = false;

    const apply = () => {
      el.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`;
    };
    const move = (e: PointerEvent) => {
      if (!rect) rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist > radius + Math.max(rect.width, rect.height) / 2) {
        tx = 0;
        ty = 0;
      } else {
        tx = Math.max(-14, Math.min(14, dx * strength));
        ty = Math.max(-10, Math.min(10, dy * strength));
      }
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    const enter = () => {
      rect = el.getBoundingClientRect();
      el.style.transition = "transform 220ms cubic-bezier(0.22,1,0.36,1)";
      window.addEventListener("pointermove", move, { passive: true });
    };
    const leave = () => {
      window.removeEventListener("pointermove", move);
      rect = null;
      tx = 0;
      ty = 0;
      el.style.transition = "transform 420ms cubic-bezier(0.22,1,0.36,1)";
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    // Keyboard focus should NOT magnetize — it would shift the focus ring.
    const blur = () => leave();

    const attach = () => {
      if (active) return;
      if (reduceMQ.matches || !hoverMQ.matches) return;
      active = true;
      el.addEventListener("pointerenter", enter);
      el.addEventListener("pointerleave", leave);
      el.addEventListener("blur", blur);
    };
    const detach = () => {
      if (!active) return;
      active = false;
      el.removeEventListener("pointerenter", enter);
      el.removeEventListener("pointerleave", leave);
      el.removeEventListener("blur", blur);
      window.removeEventListener("pointermove", move);
      cancelAnimationFrame(raf);
      el.style.transform = "";
      el.style.transition = "";
    };

    attach();
    const onMQ = () => {
      detach();
      attach();
    };
    reduceMQ.addEventListener?.("change", onMQ);
    hoverMQ.addEventListener?.("change", onMQ);

    return () => {
      reduceMQ.removeEventListener?.("change", onMQ);
      hoverMQ.removeEventListener?.("change", onMQ);
      detach();
    };
  }, [strength, radius]);

  return ref;
}
